import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Helmet } from 'react-helmet-async';
import Select from 'react-select';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import countryList from 'react-select-country-list';
import './custom-phone-input.css';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Lock, Phone, Globe, UploadCloud, Eye, EyeOff, Loader, CheckCircle, XCircle, Camera } from 'lucide-react';

const PersonalInfo = () => {
  const [formData, setFormData] = useState({
    name: '',
    oldPassword: '',
    newPassword: '',
    oldPin: '',
    newPin: '',
    profilePicture: null, // Store file object or URL
    username: '',
    mobile: '',
    country: '',
  });
  const [message, setMessage] = useState(null); // State for success/error messages
  const [messageType, setMessageType] = useState(null); // 'success' or 'error'
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showOldPin, setShowOldPin] = useState(false);
  const [showNewPin, setShowNewPin] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const { user, token, login } = useAuth(); // Get user and token
  const countryOptions = useMemo(() => countryList().getData(), []);

  // Initialize form data with user's current info when available
  useEffect(() => {
    if (user) {
      const countryExists = !!countryOptions.find(c => c.value === user.country);
      setFormData(prevData => ({
        ...prevData,
        name: user.name || '',
        profilePicture: user.profilePictureUrl || null,
        username: user.username || '',
        mobile: user.mobile || '',
        country: countryExists ? user.country : '',
      }));
    }
  }, [user, countryOptions]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    setMessage(null); // Clear messages on input change
    setMessageType(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        profilePicture: file,
      });
      setMessage(null);
      setMessageType(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    setMessageType(null);
    setLoading(true);

    if (!token) {
        setMessage('Authentication token missing. Please log in again.');
        setMessageType('error');
      setLoading(false);
      return;
    }

    try {
      const formDataToSubmit = new FormData();

      // Append fields only if they have changed
      if (formData.name !== user.name) {
        formDataToSubmit.append('name', formData.name);
      }
      if (formData.mobile !== user.mobile) {
        formDataToSubmit.append('mobile', formData.mobile);
      }
      if (formData.country !== user.country) {
        formDataToSubmit.append('country', formData.country);
      }
      
      // Handle password change
    if (formData.oldPassword && formData.newPassword) {
        if (formData.newPassword.length < 6) {
          throw new Error('New password must be at least 6 characters long.');
        }
        formDataToSubmit.append('oldPassword', formData.oldPassword);
        formDataToSubmit.append('newPassword', formData.newPassword);
    } else if (formData.oldPassword || formData.newPassword) {
        throw new Error('Please fill both old and new password fields to change the password.');
      }

      // Handle profile picture
      if (formData.profilePicture && typeof formData.profilePicture !== 'string') {
        formDataToSubmit.append('profilePicture', formData.profilePicture);
      }
      
      // Check if there is anything to update
      if ([...formDataToSubmit.entries()].length === 0) {
        setMessage("No changes to update.");
        setMessageType('success');
        setLoading(false);
        return;
      }

      const response = await fetch('/api/profile', {
          method: 'PUT',
          headers: {
          'Authorization': `Bearer ${token}`,
          },
        body: formDataToSubmit,
        });

        const data = await response.json();

        if (!response.ok) {
        throw new Error(data.msg || 'Failed to update profile.');
      }

      setMessage(data.msg || 'Profile updated successfully!');
      setMessageType('success');

      // Refresh user data in context to show the update instantly
      const res = await fetch('/api/profile/me', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const latestUser = await res.json();
      if (login) {
        login(token, latestUser);
      }

    } catch (err) {
      setMessage(err.message || 'An error occurred during the update.');
      setMessageType('error');
    } finally {
      setLoading(false);
      setFormData(prev => ({ ...prev, oldPassword: '', newPassword: '' }));
    }
  };

  const togglePasswordVisibility = (field) => {
    switch (field) {
      case 'oldPassword':
        setShowOldPassword(!showOldPassword);
        break;
      case 'newPassword':
        setShowNewPassword(!showNewPassword);
        break;
      case 'oldPin':
        setShowOldPin(!showOldPin);
        break;
      case 'newPin':
        setShowNewPin(!showNewPin);
        break;
      default:
        break;
    }
  };

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: 'rgba(31, 41, 55, 0.3)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '0.75rem',
      padding: '0.35rem',
      color: 'white',
      boxShadow: 'none',
      '&:hover': {
        borderColor: 'rgba(255, 255, 255, 0.2)',
      }
    }),
    singleValue: (provided) => ({
      ...provided,
      color: 'white',
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: '#1f2937',
      border: '1px solid rgba(255, 255, 255, 0.1)',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#06b6d4' : state.isFocused ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
      color: 'white',
    }),
    input: (provided) => ({
      ...provided,
      color: 'white',
    }),
  };

  return (
    <>
    <Helmet>
      <title>Personal Information | RatingApp</title>
      <meta name="description" content="Update your personal information and account details on RatingApp." />
    </Helmet>
    <div className="w-full pb-28 from-gray-900 via-blue-900/30 to-gray-900 text-white font-sans">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <motion.h1
          className="text-4xl font-extrabold tracking-tighter bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-500 text-center mb-2"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          Personal Information
        </motion.h1>
        <p className="text-slate-400 text-center mb-8">Manage your account details and security settings.</p>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Profile Picture Upload */}
          <div className="flex flex-col items-center gap-4">
            <div className="relative">
              <img
                src={typeof formData.profilePicture === 'object' && formData.profilePicture ? URL.createObjectURL(formData.profilePicture) : formData.profilePicture || `https://api.dicebear.com/7.x/initials/svg?seed=${user?.name || user?.username || 'U'}`}
                alt="Profile"
                className="w-32 h-32 rounded-full object-cover border-4 border-white/20 shadow-lg"
            />
             <button
              type="button"
                onClick={() => fileInputRef.current.click()}
                className="absolute bottom-0 right-0 bg-cyan-500 text-white p-2 rounded-full hover:bg-cyan-400 transition-colors"
              >
                <Camera size={20} />
            </button>
              <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" accept="image/*" />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Name */}
            <InputField label="Name" name="name" value={formData.name} onChange={handleChange} icon={<User />} />
            {/* Username */}
            <InputField label="Username" name="username" value={formData.username} icon={<User />} disabled />
          </div>

          {/* Mobile */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Mobile</label>
            <PhoneInput
              international
              defaultCountry="US"
              value={formData.mobile}
              onChange={(value) => setFormData({ ...formData, mobile: value })}
              className="phone-input-modern"
            />
          </div>
          
          {/* Country */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Country</label>
            <Select
              options={countryOptions}
              value={countryOptions.find(c => c.value === formData.country) || null}
              onChange={option => setFormData({...formData, country: option ? option.value : ''})}
              styles={customSelectStyles}
              placeholder="Select a country"
            />
          </div>

          <hr className="border-white/10" />

          <h2 className="text-2xl font-bold text-center text-white">Security</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Old and New Password */}
            <PasswordField label="Old Password" name="oldPassword" value={formData.oldPassword} onChange={handleChange} />
            <PasswordField label="New Password" name="newPassword" value={formData.newPassword} onChange={handleChange} />
          </div>

          <motion.button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold text-lg py-4 rounded-2xl shadow-lg shadow-cyan-500/20 border-t border-cyan-400/50 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
            whileHover={{ scale: 1.02, boxShadow: '0 20px 30px -10px rgba(0, 255, 255, 0.25)' }}
            whileTap={{ scale: 0.98 }}
          >
            {loading ? <><Loader size={20} className="animate-spin" /> Updating...</> : 'Update Profile'}
          </motion.button>
          
          <AnimatePresence>
            {message && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className={`text-center p-3 rounded-lg flex items-center justify-center gap-2 ${messageType === 'success' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}
              >
                {messageType === 'success' ? <CheckCircle size={20} /> : <XCircle size={20} />}
                <span>{message}</span>
              </motion.div>
            )}
          </AnimatePresence>
        </form>
      </div>
    </div>
    </>
  );
};

// Helper components for form fields
const InputField = ({ label, name, value, onChange, icon, disabled = false }) => (
  <div>
    <label htmlFor={name} className="block text-sm font-medium text-slate-300 mb-2">{label}</label>
    <div className="relative">
      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">{icon}</span>
      <input
        type="text"
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        disabled={disabled}
        className="w-full bg-gray-800/30 backdrop-blur-lg border border-white/10 rounded-xl py-3 pl-10 pr-4 text-white placeholder-slate-400 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all disabled:opacity-60 disabled:bg-gray-900/50 disabled:cursor-not-allowed"
      />
    </div>
  </div>
);

const PasswordField = ({ label, name, value, onChange }) => {
  const [visible, setVisible] = useState(false);
  return (
    <div>
      <label htmlFor={name} className="block text-sm font-medium text-slate-300 mb-2">{label}</label>
      <div className="relative">
        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400"><Lock /></span>
        <input
          type={visible ? 'text' : 'password'}
          id={name}
          name={name}
          value={value}
          onChange={onChange}
          className="w-full bg-gray-800/30 backdrop-blur-lg border border-white/10 rounded-xl py-3 pl-10 pr-10 text-white placeholder-slate-400 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all"
        />
        <button type="button" onClick={() => setVisible(!visible)} className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-white">
          {visible ? <EyeOff /> : <Eye />}
          </button>
        </div>
    </div>
  );
};

export default PersonalInfo;
